import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { applyFabricTexture, updateTextureZoom, remapExtrudeUVs } from './fabricTexture';
import InteractionHints from './InteractionHints';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const NORM = 125;
const DEFAULT_CAM = { x: 0, y: 1.36, z: 1.62 };

/**
 * Build an L-shaped 2D profile for extrusion.
 * a = long width (A), b = full depth (B), c = back width (C), d = short side depth (D)
 * facing = 'left' means the wing extends to the LEFT; 'right' means RIGHT.
 *
 * L-shape (left-facing, top-down):
 *   Front edge at y = -halfB, width A
 *   Step on the RIGHT side at y = -halfB + d
 *   Back edge at y = +halfB, width C (left portion)
 */
function createLShape(a, b, c, d, facing) {
  const halfA = a / 2, halfC = c / 2, halfB = b / 2;
  const wingDepth = Math.max(0.01, b - d);
  const stepY = -halfB + wingDepth;
  const r = Math.max(0.0005, Math.min(2 / NORM, Math.abs(a - c) * 0.25, wingDepth * 0.25, d * 0.25));

  const s = new THREE.Shape();
  if (facing === 'right') {
    // Wing extends to the right: back edge (width C) on right side, step cuts off LEFT side
    s.moveTo(-halfA + r, -halfB);
    s.lineTo(halfA - r, -halfB);
    s.absarc(halfA - r, -halfB + r, r, -Math.PI / 2, 0, false);
    s.lineTo(halfA, halfB - r);
    s.absarc(halfA - r, halfB - r, r, 0, Math.PI / 2, false);
    s.lineTo(halfA - c + r, halfB);
    s.absarc(halfA - c + r, halfB - r, r, Math.PI / 2, Math.PI, false);
    s.lineTo(halfA - c, stepY + r);
    s.absarc(halfA - c - r, stepY + r, r, 0, -Math.PI / 2, true);
    s.lineTo(-halfA + r, stepY);
    s.absarc(-halfA + r, stepY - r, r, Math.PI / 2, Math.PI, false);
    s.lineTo(-halfA, -halfB + r);
    s.absarc(-halfA + r, -halfB + r, r, Math.PI, 3 * Math.PI / 2, false);
  } else {
    // Wing extends to the left: back edge (width C) on left side, step cuts off RIGHT side
    s.moveTo(-halfA + r, -halfB);
    s.lineTo(halfA - r, -halfB);
    s.absarc(halfA - r, -halfB + r, r, -Math.PI / 2, 0, false);
    s.lineTo(halfA, stepY - r);
    s.absarc(halfA - r, stepY - r, r, 0, Math.PI / 2, false);
    s.lineTo(-halfA + c + r, stepY);
    s.absarc(-halfA + c + r, stepY + r, r, -Math.PI / 2, Math.PI, true);
    s.lineTo(-halfA + c, halfB - r);
    s.absarc(-halfA + c - r, halfB - r, r, 0, Math.PI / 2, false);
    s.lineTo(-halfA + r, halfB);
    s.absarc(-halfA + r, halfB - r, r, Math.PI / 2, Math.PI, false);
    s.lineTo(-halfA, -halfB + r);
    s.absarc(-halfA + r, -halfB + r, r, Math.PI, 3 * Math.PI / 2, false);
  }
  return s;
}

function createRectShape(w, d) {
  const hw = w / 2, hd = d / 2;
  const r = Math.max(0.0005, Math.min(2 / NORM, hw * 0.15, hd * 0.15));
  const s = new THREE.Shape();
  s.moveTo(-hw + r, -hd);
  s.lineTo(hw - r, -hd);
  s.absarc(hw - r, -hd + r, r, -Math.PI / 2, 0, false);
  s.lineTo(hw, hd - r);
  s.absarc(hw - r, hd - r, r, 0, Math.PI / 2, false);
  s.lineTo(-hw + r, hd);
  s.absarc(-hw + r, hd - r, r, Math.PI / 2, Math.PI, false);
  s.lineTo(-hw, -hd + r);
  s.absarc(-hw + r, -hd + r, r, Math.PI, 3 * Math.PI / 2, false);
  return s;
}

function buildGeo(shape, t) {
  const bev = Math.min(t * 0.06, 0.003);
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: t, bevelEnabled: true, bevelThickness: bev,
    bevelSize: bev, bevelSegments: 3, curveSegments: 48
  });
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, t / 2, 0);
  remapExtrudeUVs(geo);
  geo.computeVertexNormals();
  return geo;
}

const LCushion3DPreview = ({
  longWidth = 90, fullDepth = 80, backWidth = 50, shortSideDepth = 60, thickness = 15, rawThickness,
  fabricColor, fabricImageUrl, fabricZoom = 3, showZip = true,
  rawA = 0, rawB = 0, rawC = 0, rawD = 0, unit = 'cm',
  activeField = null,
  facing = 'left', showMiddle = false, middleWidth = 30,
  pipingOption = 'none', pipingColor, pipingImageUrl
}) => {
  const mountRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cushionRef = useRef(null);
  const cushion2Ref = useRef(null);
  const middleRef = useRef(null);
  const zipRef = useRef(null);
  const zipRef2 = useRef(null);
  const zipMidRef = useRef(null);
  const dimLinesRef = useRef(null);
  const dimLabelsRef = useRef({ a: null, b: null, c: null, d: null, t: null, mw: null, tw: null });
  const currentDimsRef = useRef(null);
  const containerSizeRef = useRef({ w: 0, h: 0 });
  const initializedRef = useRef(false);
  const animFrameRef = useRef(null);
  const targetDimsRef = useRef(null);
  const resetCameraRef = useRef(true);
  const showZipRef = useRef(showZip);
  const activeFieldRef = useRef(activeField);
  const facingRef = useRef(facing);
  const rawARef = useRef(rawA);
  const rawMWRef = useRef(showMiddle ? middleWidth : 0);
  const showMiddleRef = useRef(showMiddle);
  const middleWidthRef = useRef(middleWidth / NORM);
  const targetMiddleWidthRef = useRef(middleWidth / NORM);
  const pipingTop1Ref = useRef(null);
  const pipingBottom1Ref = useRef(null);
  const pipingTop2Ref = useRef(null);
  const pipingBottom2Ref = useRef(null);
  const pipingTopMidRef = useRef(null);
  const pipingBottomMidRef = useRef(null);

  useEffect(() => { showZipRef.current = showZip; }, [showZip]);
  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);
  useEffect(() => { facingRef.current = facing; resetCameraRef.current = true; }, [facing]);
  useEffect(() => { rawARef.current = rawA; }, [rawA]);
  useEffect(() => { rawMWRef.current = showMiddle ? middleWidth : 0; }, [showMiddle, middleWidth]);
  useEffect(() => { showMiddleRef.current = showMiddle; resetCameraRef.current = true; }, [showMiddle]);
  useEffect(() => { targetMiddleWidthRef.current = middleWidth / NORM; }, [middleWidth]);

  useEffect(() => {
    if (!mountRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = mountRef.current;
    const w = container.clientWidth, h = container.clientHeight;
    containerSizeRef.current = { w, h };

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f0eb);

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.target.set(0, 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dir = new THREE.DirectionalLight(0xffffff, 1.2);
    dir.position.set(3, 5, 3);
    dir.castShadow = true;
    dir.shadow.mapSize.set(1024, 1024);
    scene.add(dir);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(10, 10),
      new THREE.ShadowMaterial({ opacity: 0.1 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // Cushion materials and meshes
    const mat = new THREE.MeshStandardMaterial({ color: 0x5a8a5a, roughness: 0.85, metalness: 0.02 });
    const a0 = longWidth / NORM, b0 = fullDepth / NORM;
    const c0 = backWidth / NORM, d0 = shortSideDepth / NORM, t0 = thickness / NORM;

    const shape1 = createLShape(a0, b0, c0, d0, 'left');
    const mesh1 = new THREE.Mesh(buildGeo(shape1, t0), mat);
    mesh1.castShadow = true;
    scene.add(mesh1);
    cushionRef.current = mesh1;

    // Second cushion (for 'both' mode)
    const mat2 = mat.clone();
    const shape2 = createLShape(a0, b0, c0, d0, 'right');
    const mesh2 = new THREE.Mesh(buildGeo(shape2, t0), mat2);
    mesh2.castShadow = true;
    mesh2.visible = false;
    scene.add(mesh2);
    cushion2Ref.current = mesh2;

    // Middle cushion
    const midMat = mat.clone();
    const midShape = createRectShape(middleWidth / NORM, b0);
    const midMesh = new THREE.Mesh(buildGeo(midShape, t0), midMat);
    midMesh.castShadow = true;
    midMesh.visible = false;
    scene.add(midMesh);
    middleRef.current = midMesh;

    currentDimsRef.current = { a: a0, b: b0, c: c0, d: d0, t: t0 };
    targetDimsRef.current = { a: a0, b: b0, c: c0, d: d0, t: t0 };

    // Zip: U-shaped (back edge + halfway along side depth from both ends)
    const trackMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    const zipBack = new THREE.Mesh(new THREE.BoxGeometry(1, 0.006, 0.008), trackMat);
    const zipLeft = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 1), trackMat);
    const zipRight = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 1), trackMat);
    const zipTeeth = Array.from({ length: 80 }, () => {
      const t2 = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 0.008), trackMat);
      scene.add(t2); return t2;
    });
    const zipPull = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.005, 0.006), new THREE.MeshBasicMaterial({ color: 0xf5f5f5 }));
    scene.add(zipBack, zipLeft, zipRight, zipPull);
    zipRef.current = { back: zipBack, left: zipLeft, right: zipRight, teeth: zipTeeth, pull: zipPull };

    // Zip 2: full U-shape for mesh2 (the second cushion shown in 'both' mode)
    const zipBack2 = new THREE.Mesh(new THREE.BoxGeometry(1, 0.006, 0.008), trackMat);
    const zipLeft2 = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 1), trackMat);
    const zipRight2 = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 1), trackMat);
    const zipTeeth2 = Array.from({ length: 80 }, () => {
      const t2 = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 0.008), trackMat);
      scene.add(t2); return t2;
    });
    const zipPull2 = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.005, 0.006), new THREE.MeshBasicMaterial({ color: 0xf5f5f5 }));
    scene.add(zipBack2, zipLeft2, zipRight2, zipPull2);
    zipRef2.current = { back: zipBack2, left: zipLeft2, right: zipRight2, teeth: zipTeeth2, pull: zipPull2 };

    // Zip middle: just a back-edge track (like the rectangle seat cushion's back zip)
    const zipBackMid = new THREE.Mesh(new THREE.BoxGeometry(1, 0.006, 0.008), trackMat);
    const zipTeethMid = Array.from({ length: 40 }, () => {
      const t2 = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 0.008), trackMat);
      scene.add(t2); return t2;
    });
    const zipPullMid = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.005, 0.006), new THREE.MeshBasicMaterial({ color: 0xf5f5f5 }));
    scene.add(zipBackMid, zipPullMid);
    zipMidRef.current = { back: zipBackMid, teeth: zipTeethMid, pull: zipPullMid };

    // Dimension lines
    const aMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const bMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const cMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const dMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const arrowGeo = new THREE.ConeGeometry(0.007, 0.015, 6);

    const aDashes = Array.from({ length: 28 }, () => { const d2 = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.003, 0.003), aMat); scene.add(d2); return d2; });
    const aA1 = new THREE.Mesh(arrowGeo, aMat); scene.add(aA1);
    const aA2 = new THREE.Mesh(arrowGeo, aMat); scene.add(aA2);

    const bDashes = Array.from({ length: 28 }, () => { const d2 = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), bMat); scene.add(d2); return d2; });
    const bA1 = new THREE.Mesh(arrowGeo, bMat); scene.add(bA1);
    const bA2 = new THREE.Mesh(arrowGeo, bMat); scene.add(bA2);

    const cDashes = Array.from({ length: 20 }, () => { const d2 = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.003, 0.003), cMat); scene.add(d2); return d2; });
    const cA1 = new THREE.Mesh(arrowGeo, cMat); scene.add(cA1);
    const cA2 = new THREE.Mesh(arrowGeo, cMat); scene.add(cA2);

    const dDashes = Array.from({ length: 22 }, () => { const d2 = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), dMat); scene.add(d2); return d2; });
    const dA1 = new THREE.Mesh(arrowGeo, dMat); scene.add(dA1);
    const dA2 = new THREE.Mesh(arrowGeo, dMat); scene.add(dA2);

    dimLinesRef.current = { aDashes, aA1, aA2, bDashes, bA1, bA2, cDashes, cA1, cA2, dDashes, dA1, dA2, aMat, bMat, cMat, dMat };

    // Total width dimension line (yellow, solid, for Both mode — in front of cushions)
    const twMat = new THREE.MeshBasicMaterial({ color: 0xffd700 });
    // Single solid line mesh instead of dashes
    const twSolidLine = new THREE.Mesh(new THREE.BoxGeometry(1, 0.003, 0.003), twMat);
    twSolidLine.visible = false;
    scene.add(twSolidLine);
    const twA1 = new THREE.Mesh(arrowGeo.clone(), twMat); twA1.visible = false; scene.add(twA1);
    const twA2 = new THREE.Mesh(arrowGeo.clone(), twMat); twA2.visible = false; scene.add(twA2);
    dimLinesRef.current.twSolidLine = twSolidLine;
    dimLinesRef.current.twA1 = twA1;
    dimLinesRef.current.twA2 = twA2;
    dimLinesRef.current.twMat = twMat;

    // Piping meshes: 6 total (top/bottom for main L, second L, and middle rect)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingTop1 = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingBottom1 = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    const pipingTop2 = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    const pipingBottom2 = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    const pipingTopMid = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    const pipingBottomMid = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    [pipingTop1, pipingBottom1, pipingTop2, pipingBottom2, pipingTopMid, pipingBottomMid].forEach(m => {
      m.visible = false;
      scene.add(m);
    });
    pipingTop1Ref.current = pipingTop1;
    pipingBottom1Ref.current = pipingBottom1;
    pipingTop2Ref.current = pipingTop2;
    pipingBottom2Ref.current = pipingBottom2;
    pipingTopMidRef.current = pipingTopMid;
    pipingBottomMidRef.current = pipingBottomMid;

    const targetCamPos = new THREE.Vector3();

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __dl = dimLinesRef.current;
        if (__dl) Object.values(__dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });
        const __zr = zipRef.current;
        if (__zr) {
          if (__zr.track) __zr.track.visible = false;
          if (__zr.teeth) __zr.teeth.forEach(t => t.visible = false);
          if (__zr.pull) __zr.pull.visible = false;
        }
        const __zr2 = zipRef2.current;
        if (__zr2) {
          if (__zr2.back) __zr2.back.visible = false;
          if (__zr2.left) __zr2.left.visible = false;
          if (__zr2.right) __zr2.right.visible = false;
          if (__zr2.teeth) __zr2.teeth.forEach(t => t.visible = false);
          if (__zr2.pull) __zr2.pull.visible = false;
        }
        const __zrm = zipMidRef.current;
        if (__zrm) {
          if (__zrm.back) __zrm.back.visible = false;
          if (__zrm.teeth) __zrm.teeth.forEach(t => t.visible = false);
          if (__zrm.pull) __zrm.pull.visible = false;
        }
      }
      controls.update();

      const cd = currentDimsRef.current;
      const td = targetDimsRef.current;
      if (!cd || !td) { renderer.render(scene, camera); return; }

      // Smooth dimension interpolation
      let needsRebuild = false;
      const next = {};
      for (const key of ['a', 'b', 'c', 'd', 't']) {
        next[key] = THREE.MathUtils.lerp(cd[key], td[key], 0.12);
        if (Math.abs(next[key] - td[key]) > 0.0001) needsRebuild = true;
      }

      const rawFac = facingRef.current;
      // Invert left/right so visual appearance matches label (camera view mirrors model X-axis)
      const fac = rawFac === 'left' ? 'right' : rawFac === 'right' ? 'left' : rawFac;
      const isBoth = fac === 'both';
      const GAP = 0.03;
      // Animate middle width
      middleWidthRef.current = THREE.MathUtils.lerp(middleWidthRef.current, targetMiddleWidthRef.current, 0.12);
      const mw = middleWidthRef.current;
      const hasMid = isBoth && showMiddleRef.current && mw > 0;
      const totalGap = hasMid ? mw + GAP * 2 : GAP;

      if (needsRebuild || true) {
        // Rebuild primary cushion
        const fac1 = isBoth ? 'right' : fac;
        mesh1.geometry.dispose();
        mesh1.geometry = buildGeo(createLShape(next.a, next.b, next.c, next.d, fac1), next.t);

        if (isBoth) {
          mesh2.visible = true;
          mesh2.geometry.dispose();
          mesh2.geometry = buildGeo(createLShape(next.a, next.b, next.c, next.d, 'left'), next.t);
          mesh1.position.x = -(next.a / 2 + totalGap / 2);
          mesh2.position.x = next.a / 2 + totalGap / 2;
        } else {
          mesh2.visible = false;
          mesh1.position.x = 0;
        }

        // Middle cushion
        if (hasMid) {
          midMesh.visible = true;
          midMesh.geometry.dispose();
          midMesh.geometry = buildGeo(createRectShape(mw, next.b), next.t);
          midMesh.position.x = 0;
        } else {
          midMesh.visible = false;
        }

        currentDimsRef.current = next;
      }

      // Floor
      floor.position.y = -cd.t / 2 - 0.002;

      // Track piping positions with lerped dims
      if (pipingTop1Ref.current) {
        const showPiping = pipingTop1Ref.current.userData.showPiping;
        const pBev = Math.min(cd.t * 0.06, 0.003);
        const topY = cd.t * 1.5 + pBev * Math.SQRT1_2;
        const botY = cd.t * 0.5 - pBev * Math.SQRT1_2;

        pipingTop1Ref.current.visible = showPiping;
        pipingBottom1Ref.current.visible = showPiping;
        pipingTop1Ref.current.position.set(mesh1.position.x, topY, 0);
        pipingBottom1Ref.current.position.set(mesh1.position.x, botY, 0);

        pipingTop2Ref.current.visible = showPiping && isBoth;
        pipingBottom2Ref.current.visible = showPiping && isBoth;
        pipingTop2Ref.current.position.set(mesh2.position.x, topY, 0);
        pipingBottom2Ref.current.position.set(mesh2.position.x, botY, 0);

        pipingTopMidRef.current.visible = showPiping && hasMid;
        pipingBottomMidRef.current.visible = showPiping && hasMid;
        pipingTopMidRef.current.position.set(0, topY, 0);
        pipingBottomMidRef.current.position.set(0, botY, 0);
      }

      // Zip positioning: U-shaped (back edge + arms running half D along side depth)
      const zr = zipRef.current;
      const isVis = showZipRef.current; // Zip 1 stays visible in single AND 'both' modes
      const halfB_z = cd.b / 2;
      const halfA_z = cd.a / 2;
      const fac1 = isBoth ? 'right' : fac;
      const armLen = cd.d / 2;
      const mx = mesh1.position.x;

      // Wing-side and inner-side corner x positions
      const wingX = fac1 === 'left' ? -halfA_z : halfA_z;
      const innerX = fac1 === 'left' ? -halfA_z + cd.c : halfA_z - cd.c;
      const backCenterX = (wingX + innerX) / 2;

      // Back track: full C width along back edge
      zr.back.visible = isVis;
      zr.back.scale.x = cd.c;
      zr.back.position.set(backCenterX + mx, cd.t, -halfB_z - 0.001);

      // Wing arm: from wing-side back corner, going forward by half D
      zr.left.visible = isVis;
      zr.left.scale.z = armLen;
      zr.left.position.set(wingX + mx, cd.t, -halfB_z + armLen / 2);

      // Inner arm: from inner-side back corner, going forward by half D
      zr.right.visible = isVis;
      zr.right.scale.z = armLen;
      zr.right.position.set(innerX + mx, cd.t, -halfB_z + armLen / 2);

      // Pull tab at end of wing arm
      zr.pull.visible = isVis;
      zr.pull.position.set(wingX + mx, cd.t + 0.004, -halfB_z + armLen + 0.008);

      // Teeth along U-path: wing arm → back → inner arm
      const totalLen = cd.c + cd.d;
      const backDir = fac1 === 'left' ? 1 : -1;
      for (let i = 0; i < zr.teeth.length; i++) {
        const tooth = zr.teeth[i];
        tooth.visible = isVis;
        const param = (i + 0.5) / zr.teeth.length * totalLen;
        const alt = (i % 2 === 0 ? 1 : -1) * 0.010;

        if (param < armLen) {
          // Wing arm: along z
          tooth.position.set(wingX + alt + mx, cd.t, (-halfB_z + armLen) - param);
        } else if (param < armLen + cd.c) {
          // Back: along x
          const bp = param - armLen;
          tooth.position.set(wingX + backDir * bp + mx, cd.t, -halfB_z + alt);
        } else {
          // Inner arm: along z
          const ip = param - armLen - cd.c;
          tooth.position.set(innerX + alt + mx, cd.t, -halfB_z + ip);
        }
      }

      // Zip 2 — second cushion (mesh2, facing 'left') in 'both' mode
      const zr2 = zipRef2.current;
      if (zr2) {
        const isVis2 = showZipRef.current && isBoth;
        const wingX2 = -halfA_z;            // facing 'left' → wing on left side
        const innerX2 = -halfA_z + cd.c;    // inner side cd.c to the right
        const backCenterX2 = (wingX2 + innerX2) / 2;
        const mx2 = mesh2.position.x;

        zr2.back.visible = isVis2;
        zr2.back.scale.x = cd.c;
        zr2.back.position.set(backCenterX2 + mx2, cd.t, -halfB_z - 0.001);

        zr2.left.visible = isVis2;
        zr2.left.scale.z = armLen;
        zr2.left.position.set(wingX2 + mx2, cd.t, -halfB_z + armLen / 2);

        zr2.right.visible = isVis2;
        zr2.right.scale.z = armLen;
        zr2.right.position.set(innerX2 + mx2, cd.t, -halfB_z + armLen / 2);

        zr2.pull.visible = isVis2;
        zr2.pull.position.set(wingX2 + mx2, cd.t + 0.004, -halfB_z + armLen + 0.008);

        // Teeth along U-path: wing arm → back → inner arm
        const totalLen2 = cd.c + cd.d;
        const backDir2 = 1; // facing 'left' → going right along back from wingX2 to innerX2
        for (let i = 0; i < zr2.teeth.length; i++) {
          const tooth = zr2.teeth[i];
          tooth.visible = isVis2;
          const param = (i + 0.5) / zr2.teeth.length * totalLen2;
          const alt = (i % 2 === 0 ? 1 : -1) * 0.010;
          if (param < armLen) {
            tooth.position.set(wingX2 + alt + mx2, cd.t, (-halfB_z + armLen) - param);
          } else if (param < armLen + cd.c) {
            const bp = param - armLen;
            tooth.position.set(wingX2 + backDir2 * bp + mx2, cd.t, -halfB_z + alt);
          } else {
            const ip = param - armLen - cd.c;
            tooth.position.set(innerX2 + alt + mx2, cd.t, -halfB_z + ip);
          }
        }
      }

      // Zip middle — straight back-edge track on the middle rectangle (like the rectangle seat cushion's back zip)
      const zrm = zipMidRef.current;
      if (zrm) {
        const isVisMid = showZipRef.current && hasMid;
        zrm.back.visible = isVisMid;
        zrm.back.scale.x = mw;
        zrm.back.position.set(0, cd.t, -halfB_z - 0.001);
        zrm.pull.visible = isVisMid;
        zrm.pull.position.set(mw / 2 - 0.02, cd.t + 0.004, -halfB_z - 0.002);
        for (let i = 0; i < zrm.teeth.length; i++) {
          const tooth = zrm.teeth[i];
          tooth.visible = isVisMid;
          const param = (i + 0.5) / zrm.teeth.length * mw;
          const alt = (i % 2 === 0 ? 1 : -1) * 0.010;
          tooth.position.set(-mw / 2 + param, cd.t, -halfB_z + alt);
        }
      }

      // Dimension lines (shown in all modes)
      const dl = dimLinesRef.current;
      if (dl) {
        const bev = Math.min(cd.t * 0.06, 0.003);
        const topY = cd.t * 1.5 + bev + 0.004;
        const halfA = cd.a / 2;
        const halfB = cd.b / 2;
        const ox = mesh1.position.x;
        const fac1 = isBoth ? 'right' : fac;

        // Show per-cushion dimension lines always
        [...dl.aDashes, dl.aA1, dl.aA2, ...dl.bDashes, dl.bA1, dl.bA2, ...dl.cDashes, dl.cA1, dl.cA2, ...dl.dDashes, dl.dA1, dl.dA2].forEach(m => { m.visible = true; });

        // A line: front edge of primary cushion
        const aZ = halfB - 0.06;
        for (let i = 0; i < dl.aDashes.length; i++) {
          dl.aDashes[i].position.set(ox - halfA + cd.a * ((i + 0.5) / dl.aDashes.length), topY, aZ);
        }
        dl.aA1.position.set(ox - halfA, topY, aZ);
        dl.aA1.rotation.set(0, 0, Math.PI / 2);
        dl.aA2.position.set(ox + halfA, topY, aZ);
        dl.aA2.rotation.set(0, 0, -Math.PI / 2);

        // B line: full depth, on wing side
        const bX = fac1 === 'left' ? ox - halfA / 3 : ox + halfA / 3;
        for (let i = 0; i < dl.bDashes.length; i++) {
          dl.bDashes[i].position.set(bX, topY, halfB - cd.b * ((i + 0.5) / dl.bDashes.length));
        }
        dl.bA1.position.set(bX, topY, halfB);
        dl.bA1.rotation.set(Math.PI / 2, 0, 0);
        dl.bA2.position.set(bX, topY, -halfB);
        dl.bA2.rotation.set(-Math.PI / 2, 0, 0);

        // C line: back width
        const cZ = -halfB + 0.08;
        const cStartX = fac1 === 'left' ? ox - halfA : ox + halfA - cd.c;
        for (let i = 0; i < dl.cDashes.length; i++) {
          dl.cDashes[i].position.set(cStartX + cd.c * ((i + 0.5) / dl.cDashes.length), topY, cZ);
        }
        dl.cA1.position.set(cStartX, topY, cZ);
        dl.cA1.rotation.set(0, 0, Math.PI / 2);
        dl.cA2.position.set(cStartX + cd.c, topY, cZ);
        dl.cA2.rotation.set(0, 0, -Math.PI / 2);

        // D line: side depth
        const dX = fac1 === 'left' ? ox - halfA + cd.c - 0.04 : ox + halfA - cd.c + 0.04;
        const dZBack = -halfB;
        const dZStep = -halfB + cd.d;
        for (let i = 0; i < dl.dDashes.length; i++) {
          dl.dDashes[i].position.set(dX, topY, dZBack + cd.d * ((i + 0.5) / dl.dDashes.length));
        }
        dl.dA1.position.set(dX, topY, dZBack);
        dl.dA1.rotation.set(-Math.PI / 2, 0, 0);
        dl.dA2.position.set(dX, topY, dZStep);
        dl.dA2.rotation.set(Math.PI / 2, 0, 0);

        // Total width line (yellow, solid, in front of all cushions, only in Both mode)
        const showTW = isBoth;
        const mesh1X = mesh1.position.x;
        const mesh2X = mesh2.position.x;
        const twLeftX = Math.min(mesh1X, mesh2X) - cd.a / 2;
        const twRightX = Math.max(mesh1X, mesh2X) + cd.a / 2;
        const twWidth = twRightX - twLeftX;
        const twCenterX = (twLeftX + twRightX) / 2;
        const twZ = halfB + 0.06;
        const twY = cd.t / 4; // Halfway up the cushion thickness
        const arrowInset = 0.0075; // Half arrow cone height
        [dl.twSolidLine, dl.twA1, dl.twA2].forEach(m => { m.visible = showTW; });
        if (showTW) {
          dl.twSolidLine.scale.set(twWidth, 1, 1);
          dl.twSolidLine.position.set(twCenterX, twY, twZ);
          dl.twA1.position.set(twLeftX + arrowInset, twY, twZ);
          dl.twA1.rotation.set(0, 0, Math.PI / 2);
          dl.twA2.position.set(twRightX - arrowInset, twY, twZ);
          dl.twA2.rotation.set(0, 0, -Math.PI / 2);
        }
      }

      // Label positions + highlight
      const { w: cw, h: ch } = containerSizeRef.current;
      const bev2 = Math.min(cd.t * 0.06, 0.003);
      const labelY = cd.t * 1.5 + bev2 + 0.004;
      const af = activeFieldRef.current;
      const YELLOW = 0xffd700;
      const WHITE = 0xffffff;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';
      const ox = mesh1.position.x;

      if (dl) {
        dl.aMat.color.set(af === 'a' ? YELLOW : WHITE);
        dl.bMat.color.set(af === 'b' ? YELLOW : WHITE);
        dl.cMat.color.set(af === 'c' ? YELLOW : WHITE);
        dl.dMat.color.set(af === 'd' ? YELLOW : WHITE);
      }

      const showLabels = true;

      if (dimLabelsRef.current.a) {
        if (showLabels) {
          const v = new THREE.Vector3(ox - (cd.a / 3), labelY, cd.b / 2 - 0.03);
          v.project(camera);
          dimLabelsRef.current.a.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
          dimLabelsRef.current.a.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
          dimLabelsRef.current.a.style.opacity = '1';
          dimLabelsRef.current.a.style.backgroundColor = af === 'a' ? activeBg : defaultBg;
          dimLabelsRef.current.a.style.color = af === 'a' ? activeText : defaultText;
        } else { dimLabelsRef.current.a.style.opacity = '0'; }
      }
      if (dimLabelsRef.current.b) {
        if (showLabels) {
          const fac1b = isBoth ? 'right' : fac;
          const bLabelX = fac1b === 'left' ? ox - cd.a / 6 : ox + cd.a / 6;
          const v = new THREE.Vector3(bLabelX + 0.04, labelY, 0);
          v.project(camera);
          dimLabelsRef.current.b.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
          dimLabelsRef.current.b.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
          dimLabelsRef.current.b.style.opacity = '1';
          dimLabelsRef.current.b.style.backgroundColor = af === 'b' ? activeBg : defaultBg;
          dimLabelsRef.current.b.style.color = af === 'b' ? activeText : defaultText;
        } else { dimLabelsRef.current.b.style.opacity = '0'; }
      }
      if (dimLabelsRef.current.c) {
        if (showLabels) {
          const fac1b = isBoth ? 'right' : fac;
          const cLabelX = fac1b === 'left' ? ox - (cd.a / 2) + cd.c / 4 : ox + (cd.a / 2) - cd.c / 4;
          const v = new THREE.Vector3(cLabelX, labelY, -(cd.b / 2) + 0.12);
          v.project(camera);
          dimLabelsRef.current.c.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
          dimLabelsRef.current.c.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
          dimLabelsRef.current.c.style.opacity = '1';
          dimLabelsRef.current.c.style.backgroundColor = af === 'c' ? activeBg : defaultBg;
          dimLabelsRef.current.c.style.color = af === 'c' ? activeText : defaultText;
        } else { dimLabelsRef.current.c.style.opacity = '0'; }
      }
      if (dimLabelsRef.current.d) {
        if (showLabels) {
          const fac1b = isBoth ? 'right' : fac;
          const dLabelX = fac1b === 'left' ? ox - cd.a / 2 + cd.c - 0.02 : ox + cd.a / 2 - cd.c + 0.02;
          const dMidZ = -(cd.b / 2) + cd.d / 2;
          const v = new THREE.Vector3(dLabelX, labelY, dMidZ);
          v.project(camera);
          dimLabelsRef.current.d.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
          dimLabelsRef.current.d.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
          dimLabelsRef.current.d.style.opacity = '1';
          dimLabelsRef.current.d.style.backgroundColor = af === 'd' ? activeBg : defaultBg;
          dimLabelsRef.current.d.style.color = af === 'd' ? activeText : defaultText;
        } else { dimLabelsRef.current.d.style.opacity = '0'; }
      }
      if (dimLabelsRef.current.t) {
        if (showLabels && !isBoth) {
          const v = new THREE.Vector3(ox + cd.a / 2 + 0.03, cd.t, cd.b / 2);
          v.project(camera);
          dimLabelsRef.current.t.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
          dimLabelsRef.current.t.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
          dimLabelsRef.current.t.style.opacity = '1';
          dimLabelsRef.current.t.style.backgroundColor = af === 't' ? activeBg : defaultBg;
          dimLabelsRef.current.t.style.color = af === 't' ? activeText : defaultText;
        } else { dimLabelsRef.current.t.style.opacity = '0'; }
      }
      // Middle width label
      if (dimLabelsRef.current.mw) {
        if (hasMid) {
          const v = new THREE.Vector3(0, labelY, cd.b / 2 + 0.02);
          v.project(camera);
          dimLabelsRef.current.mw.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
          dimLabelsRef.current.mw.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
          dimLabelsRef.current.mw.style.opacity = '1';
          dimLabelsRef.current.mw.style.backgroundColor = af === 'mw' ? activeBg : defaultBg;
          dimLabelsRef.current.mw.style.color = af === 'mw' ? activeText : defaultText;
        } else { dimLabelsRef.current.mw.style.opacity = '0'; }
      }
      // Total width label (yellow, in front of cushions, only in Both mode)
      if (dimLabelsRef.current.tw) {
        if (isBoth) {
          const curRawA = rawARef.current;
          const curRawMW = rawMWRef.current;
          const totalWidthCm = hasMid ? curRawA * 2 + curRawMW : curRawA * 2;
          const v = new THREE.Vector3(-cd.a * 0.4, cd.t / 4, cd.b / 2 + 0.06);
          v.project(camera);
          dimLabelsRef.current.tw.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
          dimLabelsRef.current.tw.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
          dimLabelsRef.current.tw.style.opacity = '1';
          const twText = totalWidthCm > 0 ? `Total Width: ${unit === 'mm' ? Math.round(totalWidthCm * 10) : totalWidthCm} ${unit}` : 'Total Width';
          dimLabelsRef.current.tw.textContent = twText;
        } else { dimLabelsRef.current.tw.style.opacity = '0'; }
      }

      // Camera
      if (resetCameraRef.current) {
        const totalWidth = isBoth ? cd.a * 2 + totalGap : cd.a;
        const maxDim = Math.max(totalWidth, cd.b);
        const isMobile = cw < 500;
        const distMult = isMobile ? 0.5 : (isBoth ? 0.77 : 1.04);
        const dist = maxDim * distMult + 0.22;
        const camScale = isMobile ? 1.0 : 1.7;
        targetCamPos.set(
          DEFAULT_CAM.x / camScale * dist,
          DEFAULT_CAM.y / camScale * dist,
          DEFAULT_CAM.z / camScale * dist
        );
        camera.position.lerp(targetCamPos, 0.1);
        controls.target.set(0, cd.t / 4, 0);
        if (camera.position.distanceTo(targetCamPos) < 0.02) { resetCameraRef.current = false; controls.saveState(); }
      }

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const nw = container.clientWidth, nh = container.clientHeight;
      containerSizeRef.current = { w: nw, h: nh };
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', onResize);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!targetDimsRef.current) return;
    const a = longWidth / NORM, b = fullDepth / NORM;
    const c = backWidth / NORM, d = shortSideDepth / NORM, t = thickness / NORM;
    targetDimsRef.current = { a, b, c, d, t };
    resetCameraRef.current = true;
  }, [longWidth, fullDepth, backWidth, shortSideDepth, thickness]);

  useEffect(() => {
    const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setFabricLoading(true);
    let pending = 0;
    const done = () => { pending--; if (pending <= 0) setFabricLoading(false); };
    if (cushionRef.current) { pending++; applyFabricTexture(cushionRef.current, fabricImageUrl, fabricColor, fabricZoom, done); }
    if (cushion2Ref.current) { pending++; applyFabricTexture(cushion2Ref.current, fabricImageUrl, fabricColor, fabricZoom, done); }
    if (middleRef.current) { pending++; applyFabricTexture(middleRef.current, fabricImageUrl, fabricColor, fabricZoom, done); }
    if (pending === 0 && needsLoad) setFabricLoading(false);
  }, [fabricColor, fabricImageUrl]);

  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
    if (cushion2Ref.current) updateTextureZoom(cushion2Ref.current, fabricZoom);
    if (middleRef.current) updateTextureZoom(middleRef.current, fabricZoom);
  }, [fabricZoom]);

  // Helper: build a closed piping TubeGeometry from a THREE.Shape
  const buildPipingTubeFromShape = (shape, bevel) => {
    const pts2D = shape.getPoints(128);
    if (pts2D.length > 1 && pts2D[0].distanceTo(pts2D[pts2D.length - 1]) < 0.0001) pts2D.pop();
    const pts3D = pts2D.map(p => new THREE.Vector3(p.x, 0, -p.y));
    const expansion = bevel * Math.SQRT1_2;
    const n = pts3D.length;
    const expanded = pts3D.map((pt, i) => {
      const prev = pts3D[(i - 1 + n) % n];
      const next = pts3D[(i + 1) % n];
      const dx = next.x - prev.x;
      const dz = next.z - prev.z;
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      const nx = -dz / len;
      const nz = dx / len;
      const dot = nx * pt.x + nz * pt.z;
      const sign = dot >= 0 ? 1 : -1;
      return new THREE.Vector3(pt.x + sign * nx * expansion, 0, pt.z + sign * nz * expansion);
    });

    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t) {
        const n2 = this.pts.length;
        const f = t * n2;
        const i = Math.floor(f) % n2;
        const a = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n2], a);
      }
    }

    const curve = new LinearClosedCurve(expanded);
    return new THREE.TubeGeometry(curve, 192, 0.003, 8, true);
  };

  // Update piping for all cushions (main L, second L, middle rect)
  useEffect(() => {
    const pt1 = pipingTop1Ref.current;
    const pb1 = pipingBottom1Ref.current;
    const pt2 = pipingTop2Ref.current;
    const pb2 = pipingBottom2Ref.current;
    const ptm = pipingTopMidRef.current;
    const pbm = pipingBottomMidRef.current;
    if (!pt1 || !pb1) return;

    const showPiping = pipingOption !== 'none';
    // Store on userData so animate loop can read it
    pt1.userData.showPiping = showPiping;

    [pt1, pb1, pt2, pb2, ptm, pbm].forEach(m => { m.visible = false; });
    if (!showPiping) { setPipingLoading(false); return; }

    const pColor = pipingOption === 'same' ? (fabricColor || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pipingMeshes = [pt1, pb1, pt2, pb2, ptm, pbm];
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = pipingMeshes.length;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    pipingMeshes.forEach(m => {
      applyFabricTexture(m, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    });

    const a = longWidth / NORM, b = fullDepth / NORM;
    const c = backWidth / NORM, d = shortSideDepth / NORM, t = thickness / NORM;
    const bevel = Math.min(t * 0.06, 0.003);
    const expansion = bevel * Math.SQRT1_2;

    const rawFac = facing;
    const fac = rawFac === 'left' ? 'right' : rawFac === 'right' ? 'left' : rawFac;
    const isBoth = fac === 'both';

    // Build piping for main L-cushion
    const fac1 = isBoth ? 'right' : fac;
    const shape1 = createLShape(a, b, c, d, fac1);
    const tubeGeo1 = buildPipingTubeFromShape(shape1, bevel);

    pt1.geometry.dispose();
    pb1.geometry.dispose();
    pt1.geometry = tubeGeo1;
    pb1.geometry = tubeGeo1.clone();
    pt1.position.y = t * 1.5 + expansion;
    pb1.position.y = t * 0.5 - expansion;

    // Build piping for second L-cushion (only in 'both' mode)
    if (isBoth) {
      const shape2 = createLShape(a, b, c, d, 'left');
      const tubeGeo2 = buildPipingTubeFromShape(shape2, bevel);
      pt2.geometry.dispose();
      pb2.geometry.dispose();
      pt2.geometry = tubeGeo2;
      pb2.geometry = tubeGeo2.clone();
      pt2.position.y = t * 1.5 + expansion;
      pb2.position.y = t * 0.5 - expansion;
    }

    // Build piping for middle rect cushion
    if (isBoth && showMiddle) {
      const mw = middleWidth / NORM;
      const rectShape = createRectShape(mw, b);
      const tubeGeoMid = buildPipingTubeFromShape(rectShape, bevel);
      ptm.geometry.dispose();
      pbm.geometry.dispose();
      ptm.geometry = tubeGeoMid;
      pbm.geometry = tubeGeoMid.clone();
      ptm.position.y = t * 1.5 + expansion;
      pbm.position.y = t * 0.5 - expansion;
    }
  }, [longWidth, fullDepth, backWidth, shortSideDepth, thickness, facing, showMiddle, middleWidth, fabricColor, pipingOption, pipingColor, pipingImageUrl, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };
  const rawMW = showMiddle ? middleWidth : 0;

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="l-cushion-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <div ref={el => dimLabelsRef.current.a = el} data-testid="dim-label-a" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawA > 0 ? `${unit === 'mm' ? Math.round(rawA * 10) : rawA} ${unit}` : `A ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.b = el} data-testid="dim-label-b" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawB > 0 ? `${unit === 'mm' ? Math.round(rawB * 10) : rawB} ${unit}` : `B ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.c = el} data-testid="dim-label-c" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawC > 0 ? `${unit === 'mm' ? Math.round(rawC * 10) : rawC} ${unit}` : `C ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.d = el} data-testid="dim-label-d" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawD > 0 ? `${unit === 'mm' ? Math.round(rawD * 10) : rawD} ${unit}` : `D ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.t = el} data-testid="dim-label-t" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawThickness ? `${unit === 'mm' ? Math.round(rawThickness * 10) : rawThickness} ${unit}` : `T ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.mw = el} data-testid="dim-label-mw" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawMW > 0 ? `${unit === 'mm' ? Math.round(rawMW * 10) : rawMW} ${unit}` : `MW ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.tw = el} data-testid="dim-label-tw" className="absolute pointer-events-none bg-yellow-400/90 backdrop-blur-sm px-2 py-0.5 rounded text-[10px] sm:text-xs font-bold text-gray-900 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        TW
      </div>
      {showZip && facing !== 'both' && (
        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
          <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
          <span className="font-medium">Zip on back edge</span>
        </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view" data-testid="reset-view-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default LCushion3DPreview;
